import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Search, PackageCheck, PackageX, RotateCcw, Package, ClipboardList, ImageOff, Check, X } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/sku-finder.css";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import PrinterSelectModal from "../components/PrinterSelectModal";
import { silentPrintBarcode, isPrintAgentAvailable } from "../components/helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
    isSavedNetworkPrinterOnline,
} from "../components/helpers/networkPrint";
import { apiFetch } from "../api/apiFetch";
import { printManualProductLabel } from "../components/helpers/manualProductLabel";

// SKU Finder — Goods In.
//
// The operative has a quantity of one product (EAN / ASIN / FNSKU) and
// needs a JliPkgID to use as the manifest reference. The BE searches Mongo
// completemanifests for a package whose ENTIRE contents are exactly that
// product at that quantity and returns one (plus the title). If the
// operative is happy it is the right product they print its barcode on the
// same 65x38mm CODE128 label the scan sheet uses. Packages are never marked
// used — the same JliPkgID can be returned again and again.
//
// Deliberately a plain form rather than HiddenScanInput: the operator has to
// TYPE a quantity, and a hardware scanner fills the focused code field like
// a keyboard anyway. The print machinery mirrors relabel.tsx.

interface FoundProduct {
    title: string | null;
    asin: string | null;
    fnsku: string | null;
    ean: string | null;
}

interface FoundPackage {
    jliPkgId: string;
    units: number;
    rows: number;
    region: string | null;
    shipmentClosed: string | null;
}

type SearchResult =
    | { outcome: "ok"; product: FoundProduct; pkg: FoundPackage; qty: number }
    | { outcome: "no_package"; product: FoundProduct; qty: number }
    | { outcome: "not_found"; qty: number };

type PrintMode = "local" | "network" | null;

const AUTO_PRINT_KEY = "sku-finder-auto-print";
const PRINT_MODE_KEY = "sku-finder-print-mode";

// ---- Manual Products mode (plan: feat-manual-products.md) ----
// Products without barcodes live on admin-uploaded lists. The operative picks
// a list, types part of an identifier / barcode / title, taps the row in
// hand, confirms, and a brand-new unique SKU is recorded and printed
// (barcode + SKU text + supplier). The same row can be confirmed any number
// of times; each confirm is a new SKU. No undo.
type Mode = "package" | "manual";
const MODE_KEY = "sku-finder-mode";
const LIST_KEY = "sku-finder-manual-list";

interface ManualList { id: number; name: string; item_count: number }
interface ManualItem {
    id: number;
    supplier: string | null;
    product_identifier: string;
    barcode: string | null;
    title: string;
    image_url: string | null;
    cost: number | string | null;
    rrp: number | string | null;
    weight_grams: number | string | null;
}
interface CreatedSku { sku: string; item: ManualItem }

const money = (v: number | string | null | undefined) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? `£${n.toFixed(2)}` : null;
};
const grams = (v: number | string | null | undefined) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? `${Math.round(n)} g` : null;
};

// Product thumbnail. With an image and an onEnlarge handler it renders as a
// button that opens the full-size pop-up (ManualLightbox); otherwise a plain
// span (placeholder icon when there is no / a broken image).
const ManualThumb = ({ url, large, onEnlarge, enlargeLabel }: {
    url: string | null;
    large?: boolean;
    onEnlarge?: (url: string) => void;
    enlargeLabel?: string;
}) => {
    const [broken, setBroken] = useState(false);
    useEffect(() => { setBroken(false); }, [url]);
    const cls = `manual-thumb ${large ? "manual-thumb--large" : ""}`;
    if (url && !broken) {
        const img = <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} />;
        if (onEnlarge) {
            return (
                <button
                    type="button"
                    className={`${cls} manual-thumb--clickable`}
                    onClick={() => onEnlarge(url)}
                    aria-label={enlargeLabel}
                    title={enlargeLabel}
                >
                    {img}
                </button>
            );
        }
        return <span className={cls}>{img}</span>;
    }
    return (
        <span className={cls}>
            <ImageOff size={large ? 48 : 24} />
        </span>
    );
};

// Full-size image pop-up. Closes on the X, Escape or a tap anywhere outside the image.
const ManualLightbox = ({ url, onClose, closeLabel }: { url: string; onClose: () => void; closeLabel: string }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className="manual-lightbox" role="dialog" aria-modal="true" data-testid="manual-lightbox" onClick={onClose}>
            <button type="button" className="manual-lightbox-close" onClick={onClose} aria-label={closeLabel} title={closeLabel}>
                <X size={28} />
            </button>
            <img src={url} alt="" className="manual-lightbox-img" onClick={e => e.stopPropagation()} />
        </div>
    );
};

const SkuFinder = () => {
    usePageTitle("sku_finder");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [code, setCode] = useState("");
    const [qty, setQty] = useState("1");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<SearchResult | null>(null);
    const codeRef = useRef<HTMLInputElement | null>(null);
    const qtyRef = useRef<HTMLInputElement | null>(null);
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    // ---- Printer state (local print agent or network relay), as relabel.tsx ----
    const [autoPrint, setAutoPrint] = useState(false);
    const [printChecking, setPrintChecking] = useState(() => localStorage.getItem(AUTO_PRINT_KEY) === "true");
    const [printMode, _setPrintMode] = useState<PrintMode>(null);
    const [printerOnline, setPrinterOnline] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [printing, setPrinting] = useState(false);
    const printModeRef = useRef<PrintMode>(null);
    useEffect(() => { printModeRef.current = printMode; }, [printMode]);
    // A print requested while no printer was connected: fires once the
    // operator has picked one in the modal.
    const pendingPrintRef = useRef<string | null>(null);
    // Same for a Manual Products label (SKU + supplier line).
    const pendingManualRef = useRef<{ sku: string; supplier: string } | null>(null);

    const setPrintMode = (mode: PrintMode) => {
        _setPrintMode(mode);
        if (mode) localStorage.setItem(PRINT_MODE_KEY, mode);
        else localStorage.removeItem(PRINT_MODE_KEY);
    };

    useEffect(() => {
        const saved = localStorage.getItem(AUTO_PRINT_KEY) === "true";
        const savedMode = localStorage.getItem(PRINT_MODE_KEY);
        if (!saved) { setPrintChecking(false); return; }

        const connectIfOnline = (online: boolean, mode: "local" | "network") => {
            if (!mountedRef.current) return;
            if (online) {
                setAutoPrint(true);
                setPrintMode(mode);
                setPrinterOnline(true);
            } else {
                setAutoPrint(false);
                _setPrintMode(null);
                setPrinterOnline(false);
            }
            setPrintChecking(false);
        };

        if (savedMode === "network" && getSavedNetworkPrinter()) {
            isSavedNetworkPrinterOnline().then(online => connectIfOnline(online, "network"));
        } else if (savedMode === "local") {
            isPrintAgentAvailable().then(online => connectIfOnline(online, "local"));
        } else {
            isPrintAgentAvailable().then(available => {
                if (available) {
                    connectIfOnline(true, "local");
                } else if (getSavedNetworkPrinter()) {
                    isSavedNetworkPrinterOnline().then(online => connectIfOnline(online, "network"));
                } else {
                    connectIfOnline(false, "local");
                }
            });
        }
    }, []);

    useEffect(() => {
        if (!autoPrint || !printMode) return;
        const checkOnline = async () => {
            const online = printMode === "local"
                ? await isPrintAgentAvailable()
                : await isSavedNetworkPrinterOnline();
            if (!mountedRef.current) return;
            setPrinterOnline(online);
            if (!online) {
                setAutoPrint(false);
                _setPrintMode(null);
            }
        };
        const interval = setInterval(checkOnline, 30_000);
        return () => clearInterval(interval);
    }, [autoPrint, printMode]);

    // Same label as the scan sheet / relabel: buildBarcodeLabel HTML with the
    // raw value for the Zebra ZPL fast-path.
    const printBarcode = useCallback(async (barcodeValue: string) => {
        setPrinting(true);
        try {
            const useNetwork = printModeRef.current === "network"
                || (!printModeRef.current && getSavedNetworkPrinter());
            if (useNetwork) {
                const saved = getSavedNetworkPrinter();
                if (saved) {
                    const { buildBarcodeLabel } = await import("../components/helpers/printAgent");
                    const r = await submitNetworkPrintJob(
                        saved.agentId,
                        saved.printerName,
                        buildBarcodeLabel(barcodeValue),
                        1,
                        barcodeValue,
                    );
                    if (!r.success) {
                        toast.dismiss();
                        toast.error(r.error || t("print_job_failed"));
                    } else {
                        toast.success(t("sku_finder_printed"), { toastId: "sku-finder-printed" });
                    }
                    return;
                }
            }

            const r = await silentPrintBarcode(barcodeValue);
            if (!r.success) {
                toast.dismiss();
                toast.error(r.error || t("print_failed"));
            } else {
                toast.success(t("sku_finder_printed"), { toastId: "sku-finder-printed" });
            }
        } finally {
            if (mountedRef.current) setPrinting(false);
        }
    }, [t]);

    const requestPrint = (barcodeValue: string) => {
        if (autoPrint && printerOnline && printMode) {
            printBarcode(barcodeValue);
            return;
        }
        // No connected printer: let the operator pick one, then print.
        pendingPrintRef.current = barcodeValue;
        toast.info(t("sku_finder_choose_printer"), { toastId: "sku-finder-pick-printer" });
        setShowPrinterSelect(true);
    };

    const onPrinterConnected = (mode: "local" | "network") => {
        setShowPrinterSelect(false);
        setAutoPrint(true);
        setPrintMode(mode);
        setPrinterOnline(true);
        localStorage.setItem(AUTO_PRINT_KEY, "true");
        const pending = pendingPrintRef.current;
        const pendingManual = pendingManualRef.current;
        pendingPrintRef.current = null;
        pendingManualRef.current = null;
        if (pending) {
            // printModeRef updates on the next render; pass the mode through
            // by setting the ref now so the first print uses the new printer.
            printModeRef.current = mode;
            printBarcode(pending);
        }
        if (pendingManual) {
            printModeRef.current = mode;
            printManual(pendingManual.sku, pendingManual.supplier);
        }
    };

    // ---- Manual Products mode ----
    const [mode, setModeState] = useState<Mode>(() => (localStorage.getItem(MODE_KEY) === "manual" ? "manual" : "package"));
    const setMode = (m: Mode) => { setModeState(m); localStorage.setItem(MODE_KEY, m); };
    const [lists, setLists] = useState<ManualList[]>([]);
    const [listsLoaded, setListsLoaded] = useState(false);
    const [listId, setListIdState] = useState<string>(() => localStorage.getItem(LIST_KEY) ?? "");
    const setListId = (id: string) => { setListIdState(id); localStorage.setItem(LIST_KEY, id); };
    const [manualQuery, setManualQuery] = useState("");
    const [manualItems, setManualItems] = useState<ManualItem[]>([]);
    const [manualLimit, setManualLimit] = useState(50);
    const [manualSearching, setManualSearching] = useState(false);
    const [selected, setSelected] = useState<ManualItem | null>(null);
    const [lightbox, setLightbox] = useState<string | null>(null);
    const closeLightbox = useCallback(() => setLightbox(null), []);
    const [created, setCreated] = useState<CreatedSku | null>(null);
    const [creating, setCreating] = useState(false);
    const manualInputRef = useRef<HTMLInputElement | null>(null);

    // Active lists, loaded when the mode is entered. The remembered list is
    // kept if it still exists, else the first one.
    useEffect(() => {
        if (mode !== "manual") return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch("/manual-products/lists", {});
                if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
                const data = await res.json().catch(() => null);
                if (!alive) return;
                const got: ManualList[] = res.ok && Array.isArray(data?.lists) ? data.lists : [];
                setLists(got);
                setListsLoaded(true);
                setListIdState(prev => {
                    if (prev && got.some(l => String(l.id) === prev)) return prev;
                    const next = got.length > 0 ? String(got[0].id) : "";
                    localStorage.setItem(LIST_KEY, next);
                    return next;
                });
                if (!res.ok) toast.error(data?.message || t("manual_products_load_failed"), { toastId: "manual-lists-failed" });
            } catch {
                if (alive) toast.error(t("manual_products_load_failed"), { toastId: "manual-lists-failed" });
            }
        })();
        return () => { alive = false; };
    }, [mode, navigate, t]);

    // Debounced search of the chosen list (≥ 2 characters).
    useEffect(() => {
        if (mode !== "manual" || selected || created) return;
        const q = manualQuery.trim();
        if (!listId || q.length < 2) { setManualItems([]); setManualSearching(false); return; }
        let alive = true;
        setManualSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiFetch(`/manual-products/search?list_id=${encodeURIComponent(listId)}&q=${encodeURIComponent(q)}`, {});
                if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
                const data = await res.json().catch(() => null);
                if (!alive) return;
                if (!res.ok || !data) {
                    toast.error(data?.message || t("manual_products_load_failed"), { toastId: "manual-search-failed" });
                    return;
                }
                setManualItems(Array.isArray(data.items) ? data.items : []);
                if (typeof data.limit === "number") setManualLimit(data.limit);
            } catch {
                if (alive) toast.error(t("manual_products_load_failed"), { toastId: "manual-search-failed" });
            } finally {
                if (alive) setManualSearching(false);
            }
        }, 250);
        return () => { alive = false; clearTimeout(timer); };
    }, [mode, listId, manualQuery, selected, created, navigate, t]);

    // Print the Manual Products label (ZPL cascade, HTML fallback).
    const printManual = useCallback(async (sku: string, supplier: string) => {
        setPrinting(true);
        try {
            const r = await printManualProductLabel(sku, supplier, printModeRef.current);
            if (!r.success) {
                toast.dismiss();
                toast.error(r.error || t("manual_products_print_failed"));
            } else {
                toast.success(t("sku_finder_printed"), { toastId: "sku-finder-printed" });
            }
        } finally {
            if (mountedRef.current) setPrinting(false);
        }
    }, [t]);

    const requestManualPrint = (sku: string, supplier: string) => {
        if (autoPrint && printerOnline && printMode) {
            printManual(sku, supplier);
            return;
        }
        pendingManualRef.current = { sku, supplier };
        toast.info(t("sku_finder_choose_printer"), { toastId: "sku-finder-pick-printer" });
        setShowPrinterSelect(true);
    };

    const confirmManual = async () => {
        if (!selected || creating) return;
        setCreating(true);
        try {
            const res = await apiFetch("/manual-products/create", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ itemId: selected.id }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.product?.sku) {
                playError();
                toast.error(data?.message || t("manual_products_create_failed"), { toastId: "manual-create-failed" });
                return;
            }
            playSuccess();
            const sku: string = data.product.sku;
            setCreated({ sku, item: selected });
            setSelected(null);
            requestManualPrint(sku, selected.supplier ?? "");
        } catch {
            if (mountedRef.current) {
                playError();
                toast.error(t("manual_products_create_failed"), { toastId: "manual-create-failed" });
            }
        } finally {
            if (mountedRef.current) setCreating(false);
        }
    };

    const nextManual = () => {
        setCreated(null);
        setSelected(null);
        setManualQuery("");
        setManualItems([]);
        setTimeout(() => manualInputRef.current?.focus(), 0);
    };

    const renderManualFields = (item: ManualItem) => {
        const rows: Array<[string, string | null]> = [
            [t("manual_products_supplier"), item.supplier],
            [t("manual_products_identifier"), item.product_identifier],
            [t("manual_products_barcode"), item.barcode],
            [t("manual_products_cost"), money(item.cost)],
            [t("manual_products_rrp"), money(item.rrp)],
            [t("manual_products_weight"), grams(item.weight_grams)],
        ];
        return (
            <div className="sku-finder-fields">
                {rows.filter(([, v]) => !!v).map(([label, value]) => (
                    <div key={label} className="sku-finder-field">
                        <span className="sku-finder-field-label">{label}</span>
                        <span className="sku-finder-field-value">{value}</span>
                    </div>
                ))}
            </div>
        );
    };

    const manualQ = manualQuery.trim();

    // ---- Search ----
    const qtyNumber = Number(qty);
    const qtyValid = Number.isInteger(qtyNumber) && qtyNumber >= 1 && qtyNumber <= 9999;
    const canSearch = code.trim().length > 0 && qtyValid && !loading;

    const doSearch = async () => {
        const trimmed = code.trim();
        if (!trimmed || !qtyValid || loading) return;
        setLoading(true);
        try {
            const res = await apiFetch("/sku-finder/search", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: trimmed, qty: qtyNumber }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                playError();
                toast.error(data?.message || t("sku_finder_failed"), { toastId: "sku-finder-failed" });
                return;
            }
            if (data.outcome === "ok" && data.package) {
                playSuccess();
                setResult({ outcome: "ok", product: data.product, pkg: data.package, qty: qtyNumber });
            } else if (data.outcome === "no_package") {
                playError();
                setResult({ outcome: "no_package", product: data.product, qty: qtyNumber });
            } else {
                playError();
                setResult({ outcome: "not_found", qty: qtyNumber });
            }
        } catch {
            if (mountedRef.current) {
                playError();
                toast.error(t("sku_finder_failed"), { toastId: "sku-finder-failed" });
            }
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    };

    const resetSearch = () => {
        setResult(null);
        setCode("");
        setQty("1");
        setTimeout(() => codeRef.current?.focus(), 0);
    };

    const renderIdentifiers = (product: FoundProduct) => {
        const rows: Array<[string, string | null]> = [
            [t("sku_finder_asin"), product.asin],
            [t("sku_finder_fnsku"), product.fnsku],
            [t("sku_finder_ean"), product.ean],
        ];
        return (
            <div className="sku-finder-fields">
                {rows.filter(([, v]) => !!v).map(([label, value]) => (
                    <div key={label} className="sku-finder-field">
                        <span className="sku-finder-field-label">{label}</span>
                        <span className="sku-finder-field-value">{value}</span>
                    </div>
                ))}
            </div>
        );
    };

    const printerTitle = printChecking
        ? t("sku_finder_printer")
        : (autoPrint && printerOnline ? t("sku_finder_printer_online") : t("sku_finder_printer_offline"));

    return (
        <div className="scan-panel sku-finder-panel">
            <div className="page-title-bar">
                <h1 className="page-title">{t("sku_finder")}</h1>
                <button
                    type="button"
                    className={`sku-finder-printer-btn ${autoPrint && printerOnline ? "is-online" : ""}`}
                    onClick={() => setShowPrinterSelect(true)}
                    title={printerTitle}
                    aria-label={printerTitle}
                >
                    <Printer size={20} />
                </button>
                <button className="page-back-btn" onClick={() => navigate("/goods-in")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            <div className="sku-finder-body">
                <div className="sku-finder-mode" role="tablist" aria-label={t("sku_finder")}>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mode === "package"}
                        className={`sku-finder-mode-btn ${mode === "package" ? "is-active" : ""}`}
                        onClick={() => setMode("package")}
                    >
                        <Package size={16} />
                        {t("sku_finder_mode_package")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mode === "manual"}
                        className={`sku-finder-mode-btn ${mode === "manual" ? "is-active" : ""}`}
                        onClick={() => setMode("manual")}
                    >
                        <ClipboardList size={16} />
                        {t("sku_finder_mode_manual")}
                    </button>
                </div>

                {mode === "package" && !result && (
                    <form
                        className="sku-finder-form"
                        onSubmit={e => { e.preventDefault(); doSearch(); }}
                    >
                        <label className="sku-finder-label" htmlFor="sku-finder-code">
                            {t("sku_finder_code_label")}
                        </label>
                        <input
                            id="sku-finder-code"
                            ref={codeRef}
                            className="sku-finder-input"
                            type="text"
                            autoFocus
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            placeholder={t("sku_finder_code_placeholder")}
                            value={code}
                            disabled={loading}
                            onChange={e => setCode(e.target.value)}
                            onKeyDown={e => {
                                // A hardware scanner ends with Enter: move on
                                // to the quantity instead of searching with
                                // whatever quantity happens to be there.
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    qtyRef.current?.focus();
                                    qtyRef.current?.select();
                                }
                            }}
                        />

                        <label className="sku-finder-label" htmlFor="sku-finder-qty">
                            {t("sku_finder_qty_label")}
                        </label>
                        <input
                            id="sku-finder-qty"
                            ref={qtyRef}
                            className="sku-finder-input sku-finder-qty-input"
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={9999}
                            step={1}
                            value={qty}
                            disabled={loading}
                            onFocus={e => e.currentTarget.select()}
                            onChange={e => setQty(e.target.value.replace(/[^0-9]/g, ""))}
                        />

                        <button
                            type="submit"
                            className="btn btn-primary btn-lg sku-finder-search-btn"
                            disabled={!canSearch}
                        >
                            <Search size={22} />
                            {loading ? t("sku_finder_searching") : t("sku_finder_search")}
                        </button>
                    </form>
                )}

                {result?.outcome === "ok" && (
                    <div className="sku-finder-result">
                        <div className="sku-finder-card sku-finder-card-ok">
                            <div className="sku-finder-card-head">
                                <PackageCheck size={22} />
                                <span>{t("sku_finder_result_title")}</span>
                            </div>
                            <h2 className="sku-finder-title">{result.product.title ?? result.product.asin ?? "—"}</h2>
                            {renderIdentifiers(result.product)}
                            <div className="sku-finder-units">
                                {t("sku_finder_units", { count: String(result.pkg.units) })}
                            </div>
                            <div className="sku-finder-pkgid-label">JliPkgID</div>
                            <div className="sku-finder-pkgid" data-testid="sku-finder-pkgid">{result.pkg.jliPkgId}</div>
                            {result.pkg.region && (
                                <div className="sku-finder-meta">
                                    {t("sku_finder_region")}: {result.pkg.region}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-primary btn-lg sku-finder-print-btn"
                            onClick={() => requestPrint(result.pkg.jliPkgId)}
                            disabled={printing}
                        >
                            <Printer size={22} />
                            {t("sku_finder_print")}
                        </button>
                        <button type="button" className="btn btn-ghost sku-finder-again-btn" onClick={resetSearch}>
                            <RotateCcw size={18} />
                            {t("sku_finder_search_again")}
                        </button>
                    </div>
                )}

                {result && result.outcome !== "ok" && (
                    <div className="sku-finder-result">
                        <div className="sku-finder-card sku-finder-card-miss">
                            <div className="sku-finder-card-head">
                                <PackageX size={22} />
                                <span>
                                    {result.outcome === "no_package"
                                        ? t("sku_finder_no_package", { qty: String(result.qty) })
                                        : t("sku_finder_not_found")}
                                </span>
                            </div>
                            {result.outcome === "no_package" && (
                                <>
                                    <h2 className="sku-finder-title">{result.product.title ?? result.product.asin ?? "—"}</h2>
                                    {renderIdentifiers(result.product)}
                                </>
                            )}
                        </div>
                        <button type="button" className="btn btn-ghost sku-finder-again-btn" onClick={resetSearch}>
                            <RotateCcw size={18} />
                            {t("sku_finder_search_again")}
                        </button>
                    </div>
                )}

                {mode === "manual" && !selected && !created && (
                    <form className="sku-finder-form" onSubmit={e => e.preventDefault()}>
                        <label className="sku-finder-label" htmlFor="sku-finder-list">
                            {t("manual_products_list_label")}
                        </label>
                        <select
                            id="sku-finder-list"
                            className="sku-finder-input sku-finder-select"
                            value={listId}
                            onChange={e => setListId(e.target.value)}
                            disabled={!listsLoaded || lists.length === 0}
                        >
                            {lists.length === 0 && <option value="">{listsLoaded ? t("manual_products_select_list") : "…"}</option>}
                            {lists.map(l => (
                                <option key={l.id} value={String(l.id)}>{l.name} ({l.item_count})</option>
                            ))}
                        </select>
                        {listsLoaded && lists.length === 0 && (
                            <div className="sku-finder-meta">{t("manual_products_no_lists")}</div>
                        )}

                        <label className="sku-finder-label" htmlFor="sku-finder-manual-q">
                            {t("manual_products_search_label")}
                        </label>
                        <input
                            id="sku-finder-manual-q"
                            ref={manualInputRef}
                            className="sku-finder-input"
                            type="text"
                            autoFocus
                            autoComplete="off"
                            spellCheck={false}
                            placeholder={t("manual_products_search_placeholder")}
                            value={manualQuery}
                            disabled={!listId}
                            onChange={e => setManualQuery(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
                        />
                        {manualQ.length > 0 && manualQ.length < 2 && (
                            <div className="sku-finder-meta">{t("manual_products_min_chars")}</div>
                        )}
                        {manualQ.length >= 2 && !manualSearching && manualItems.length === 0 && (
                            <div className="sku-finder-meta">{t("manual_products_no_matches")}</div>
                        )}
                        {manualItems.length > 0 && (
                            <ul className="manual-results" data-testid="manual-results">
                                {manualItems.map(item => (
                                    <li key={item.id} className="manual-result">
                                        <ManualThumb url={item.image_url} onEnlarge={setLightbox} enlargeLabel={t("manual_products_enlarge_image")} />
                                        <button type="button" className="manual-result-main" onClick={() => setSelected(item)}>
                                            <span className="manual-result-text">
                                                <span className="manual-result-title">{item.title}</span>
                                                <span className="manual-result-meta">
                                                    {[item.supplier, item.product_identifier, item.barcode].filter(Boolean).join(" · ")}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {manualItems.length >= manualLimit && (
                            <div className="sku-finder-meta">{t("manual_products_more_results", { count: String(manualLimit) })}</div>
                        )}
                    </form>
                )}

                {mode === "manual" && selected && !created && (
                    <div className="sku-finder-result">
                        <div className="sku-finder-card sku-finder-card-ok">
                            <div className="sku-finder-card-head">
                                <PackageCheck size={22} />
                                <span>{t("manual_products_confirm_title")}</span>
                            </div>
                            <ManualThumb url={selected.image_url} large onEnlarge={setLightbox} enlargeLabel={t("manual_products_enlarge_image")} />
                            <h2 className="sku-finder-title">{selected.title}</h2>
                            {renderManualFields(selected)}
                        </div>
                        <button
                            type="button"
                            className="btn btn-primary btn-lg sku-finder-print-btn"
                            onClick={confirmManual}
                            disabled={creating}
                        >
                            <Check size={22} />
                            {creating ? t("manual_products_creating") : t("manual_products_confirm")}
                        </button>
                        <button type="button" className="btn btn-ghost sku-finder-again-btn" onClick={() => setSelected(null)} disabled={creating}>
                            <ArrowLeft size={18} />
                            {t("manual_products_back")}
                        </button>
                    </div>
                )}

                {mode === "manual" && created && (
                    <div className="sku-finder-result">
                        <div className="sku-finder-card sku-finder-card-ok">
                            <div className="sku-finder-card-head">
                                <PackageCheck size={22} />
                                <span>{t("manual_products_created_title")}</span>
                            </div>
                            <div className="sku-finder-pkgid-label">SKU</div>
                            <div className="sku-finder-pkgid" data-testid="manual-created-sku">{created.sku}</div>
                            <h2 className="sku-finder-title">{created.item.title}</h2>
                            {renderManualFields(created.item)}
                        </div>
                        <button type="button" className="btn btn-primary btn-lg sku-finder-print-btn" onClick={nextManual}>
                            <RotateCcw size={22} />
                            {t("manual_products_next")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary sku-finder-again-btn"
                            onClick={() => requestManualPrint(created.sku, created.item.supplier ?? "")}
                            disabled={printing}
                        >
                            <Printer size={18} />
                            {t("manual_products_print_again")}
                        </button>
                    </div>
                )}
            </div>

            {lightbox && <ManualLightbox url={lightbox} onClose={closeLightbox} closeLabel={t("close")} />}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={autoPrint}
                    printMode={printMode}
                    onSelect={() => onPrinterConnected("network")}
                    onConnectLocal={() => onPrinterConnected("local")}
                    onDisconnect={() => {
                        setShowPrinterSelect(false);
                        setAutoPrint(false);
                        setPrintMode(null);
                        setPrinterOnline(false);
                        pendingPrintRef.current = null;
                        pendingManualRef.current = null;
                        localStorage.setItem(AUTO_PRINT_KEY, "false");
                    }}
                    onClose={() => { pendingPrintRef.current = null; pendingManualRef.current = null; setShowPrinterSelect(false); }}
                />
            )}
        </div>
    );
};

export default SkuFinder;
